import { DateTime } from "luxon";
import type { AppointmentListFragment } from "@/gql/graphql";
import { timezoneUtils } from "@/lib/timezone";

export interface ServiceWithTiming {
  id: string;
  serviceName: string;
  serviceDescription?: string | null;
  serviceDuration: number; // minutes
  sequenceOrder: number;
  startTime: DateTime; // calculated start time for this service
  endTime: DateTime; // calculated end time for this service
  totalPrice: number;
  worker?: {
    id: string;
    color: string;
    displayName: string;
  } | null;
  // Original appointment service data
  originalService: NonNullable<AppointmentListFragment["appointmentServices"]>[0];
}

/**
 * Calculate individual service timings based on appointment start time and service sequence
 * Services run sequentially, with each service starting when the previous one ends
 */
export function calculateServiceTimings(appointment: AppointmentListFragment): ServiceWithTiming[] {
  const appointmentStart = timezoneUtils.fromISO(appointment.startTime);
  let currentTime = appointmentStart;

  if (!appointment.appointmentServices || appointment.appointmentServices.length === 0) {
    return [];
  }

  // Sort services by sequence order to ensure proper timing calculation
  const sortedServices = [...appointment.appointmentServices].sort(
    (a, b) => a.sequenceOrder - b.sequenceOrder
  );

  return sortedServices.map((service) => {
    const serviceStart = currentTime;
    const serviceEnd = currentTime.plus({ minutes: service.serviceDuration });

    const serviceWithTiming: ServiceWithTiming = {
      id: service.id,
      serviceName: service.serviceName,
      serviceDescription: service.serviceDescription,
      serviceDuration: service.serviceDuration,
      sequenceOrder: service.sequenceOrder,
      startTime: serviceStart,
      endTime: serviceEnd,
      totalPrice: service.totalPrice,
      worker: service.worker
        ? {
            id: service.worker.id,
            color: service.worker.color ?? "",
            displayName: service.worker.displayName,
          }
        : null,
      originalService: service,
    };

    // Move to next service start time
    currentTime = serviceEnd;

    return serviceWithTiming;
  });
}

/**
 * Get the display text for a worker
 */
export function getWorkerDisplayText(
  worker?: ServiceWithTiming["worker"],
  compact = false
): string {
  if (!worker) {
    return "Unassigned";
  }

  const displayName = worker.displayName.trim();

  if (compact) {
    // Return initials for compact display
    return displayName
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join("");
  }

  // Return full name for normal display
  return displayName;
}

/**
 * Calculate positioning for a service segment within the calendar grid
 */
export interface ServicePosition {
  top: number; // pixels from calendar start
  height: number; // pixels height
  startTimeDisplay: string; // formatted start time
  durationDisplay: string; // formatted duration
}

export function calculateServicePosition(
  service: ServiceWithTiming,
  _calendarStartHour: number = 0,
  pixelsPerHour: number = 48
): ServicePosition {
  // For VStack layout, we don't need precise calendar grid positioning
  // Services are positioned relative to their parent appointment card
  const pixelsPerMinute = pixelsPerHour / 60;

  return {
    top: 0, // Position handled by flex layout in parent
    height: Math.max(service.serviceDuration * pixelsPerMinute, 16), // Minimum 16px
    startTimeDisplay: service.startTime.toFormat("HH:mm"),
    durationDisplay: `${service.serviceDuration}min`,
  };
}

/**
 * Determine the appropriate display mode based on available height
 */
export type ServiceDisplayMode = "minimal" | "compact" | "normal" | "expanded";

export function getServiceDisplayMode(heightInPixels: number): ServiceDisplayMode {
  if (heightInPixels < 24) return "minimal";
  if (heightInPixels < 35) return "compact";
  if (heightInPixels < 50) return "normal";
  return "expanded";
}

/**
 * Check if services should be displayed as individual segments or combined
 */
export function shouldDisplayAsServiceSegments(
  appointment: AppointmentListFragment,
  minHeightPerService: number = 24,
  pixelsPerHour: number = 48
): boolean {
  const services = appointment.appointmentServices;
  if (!services || services.length <= 1) {
    return false;
  }

  // Calculate total appointment duration
  const appointmentStart = timezoneUtils.fromISO(appointment.startTime);
  const appointmentEnd = timezoneUtils.fromISO(appointment.endTime);
  const totalDurationMinutes = appointmentEnd.diff(appointmentStart, "minutes").minutes;

  // If total height would be too small for individual segments, combine them
  const totalHeightPixels = (totalDurationMinutes / 60) * pixelsPerHour; // dynamic pixels per hour
  const requiredHeightForSegments = services.length * minHeightPerService;

  return totalHeightPixels >= requiredHeightForSegments;
}

/**
 * Get a summary of the appointment services for display
 */
export interface ServicesSummary {
  serviceCount: number;
  totalDuration: number;
  uniqueWorkers: Array<{ id: string; name: string; color: string }>;
  servicesText: string;
}

export function getServicesSummary(servicesWithTiming: ServiceWithTiming[]): ServicesSummary {
  const serviceCount = servicesWithTiming.length;
  const totalDuration = servicesWithTiming.reduce(
    (sum, service) => sum + service.serviceDuration,
    0
  );

  // Get unique workers
  const workerMap = new Map();
  servicesWithTiming.forEach((service) => {
    if (service.worker) {
      workerMap.set(service.worker.id, {
        id: service.worker.id,
        name: getWorkerDisplayText(service.worker),
        color: service.worker.color,
      });
    }
  });
  const uniqueWorkers = Array.from(workerMap.values());

  // Create services text
  const servicesText =
    serviceCount > 1 ? `${serviceCount} services` : servicesWithTiming[0]?.serviceName || "Service";

  return {
    serviceCount,
    totalDuration,
    uniqueWorkers,
    servicesText,
  };
}
